/**
 * Helpers for the candidate-facing offer flow's identity verification.
 *
 * The candidate authenticates via OTP at `/offer/:token`, receiving a
 * short-lived verification token (offer session). When that token expires
 * mid-flow, the public accept/decline endpoints reject the request and tag the
 * error with `data: { code: "verification_required" }`. Instead of stranding
 * the candidate with a generic error, we send them back to the OTP page to
 * verify again — the "sign in" equivalent for this context (NOT the admin
 * /login).
 */

/**
 * Discriminator the server puts on `ORPCError.data.code` when the candidate's
 * verification token is expired/invalid. Mirrors `server/rpc/routes/offers.ts`.
 */
const OFFER_REVERIFICATION_CODE = "verification_required";

/**
 * True when an offer-flow error means the verification token is expired/invalid
 * and the candidate must re-verify via OTP.
 */
function needsOfferReverification(error: unknown): boolean {
  return !!(
    error &&
    typeof error === "object" &&
    "data" in error &&
    (error as { data?: { code?: string } }).data?.code === OFFER_REVERIFICATION_CODE
  );
}

/**
 * Standard `onError` handler for the candidate offer-flow mutations
 * (accept/decline). When the verification token is expired/invalid, sends the
 * candidate back to the OTP page via a full navigation — so any stale in-memory
 * offer/session state is discarded and the OTP flow restarts cleanly. Otherwise
 * runs `fallback` (typically a toast).
 */
export function handleOfferMutationError(
  error: unknown,
  token: string | undefined,
  fallback: () => void,
): void {
  if (token && needsOfferReverification(error)) {
    window.location.href = `/offer/${token}`;
    return;
  }
  fallback();
}
